import React, { useState, useEffect } from 'react';
import BudgetHeader from './BudgetHeader';
import BudgetResults from './BudgetResults';
import CategoryBudgetControls from './CategoryBudgetControls';
import { calculateBudget } from '../utils/calculateBudget';
import { VENUE_STYLES } from '../data/venueData';
import { US_COST_RANGES, MEXICO_COST_RANGES } from './CategoryBudgetControls';

const CATEGORIES = [
  'venue',
  'catering',
  'photographer',
  'dj',
  'flowers',
  'transport'
];

const INITIAL_CATEGORY_BUDGETS = {
  venue: 50,
  catering: 50,
  photographer: 50,
  dj: 50,
  flowers: 50,
  transport: 50
};

// Wedding Extras Data
const EXTRAS = [
  { key: 'accommodations', label: 'Accommodations', unit: 'per night', us: { zero: 0, low: 100, mid: 200, high: 400, luxury: 700 }, max: 1000 },
  { key: 'cake', label: 'Cake', unit: '', us: { zero: 0, low: 300, mid: 600, high: 1000, luxury: 1500 }, max: 2000 },
  { key: 'bar', label: 'Bar / Alcohol', unit: 'per guest', us: { zero: 0, low: 15, mid: 30, high: 50, luxury: 100 }, max: 150 },
  { key: 'rentals', label: 'Rentals', unit: '', us: { zero: 0, low: 500, mid: 1500, high: 4000, luxury: 7000 }, max: 10000 },
  { key: 'lighting', label: 'Lighting / AV', unit: '', us: { zero: 0, low: 500, mid: 1500, high: 4000, luxury: 7000 }, max: 10000 },
  { key: 'hairMakeup', label: 'Hair & Makeup', unit: '', us: { zero: 0, low: 150, mid: 400, high: 700, luxury: 1500 }, max: 2500 },
  { key: 'stationery', label: 'Stationery', unit: 'per invite', us: { zero: 0, low: 1, mid: 4, high: 7, luxury: 15 }, max: 20 },
  { key: 'attire', label: 'Attire (Bride)', unit: '', us: { zero: 0, low: 500, mid: 1500, high: 3000, luxury: 6000 }, max: 10000 },
  { key: 'rehearsal', label: 'Rehearsal Dinner', unit: 'per guest', us: { zero: 0, low: 30, mid: 60, high: 150, luxury: 250 }, max: 300 },
  { key: 'brunch', label: 'Farewell Brunch', unit: 'per guest', us: { zero: 0, low: 15, mid: 40, high: 75, luxury: 150 }, max: 200 },
  { key: 'childcare', label: 'Childcare', unit: 'per hour', us: { zero: 0, low: 20, mid: 35, high: 60, luxury: 100 }, max: 150 },
  { key: 'security', label: 'Security', unit: 'per hour', us: { zero: 0, low: 25, mid: 60, high: 150, luxury: 250 }, max: 300 },
  { key: 'favors', label: 'Favors / Gifts', unit: 'per guest', us: { zero: 0, low: 1, mid: 4, high: 7, luxury: 15 }, max: 20 },
  { key: 'photobooth', label: 'Photo Booth', unit: '', us: { zero: 0, low: 300, mid: 600, high: 1000, luxury: 2000 }, max: 2500 },
  { key: 'insurance', label: 'Insurance', unit: '', us: { zero: 0, low: 150, mid: 300, high: 600, luxury: 1000 }, max: 1500 }
];

// Group extras into logical categories
const EXTRA_GROUPS = [
  { title: 'Food & Drink', extras: ['cake', 'bar', 'rehearsal', 'brunch'] },
  { title: 'Decor & Rentals', extras: ['rentals', 'lighting', 'stationery', 'favors', 'photobooth'] },
  { title: 'Attire & Beauty', extras: ['attire', 'hairMakeup'] },
  { title: 'Events & Activities', extras: ['accommodations', 'childcare', 'security'] },
  { title: 'Services & Insurance', extras: ['insurance'] }
];

const INITIAL_EXTRAS_BUDGETS = Object.fromEntries(EXTRAS.map(e => [e.key, 0]));

function getValidInitialStyle(initialStyle) {
  const validValues = VENUE_STYLES.map(s => s.value);
  return validValues.includes(initialStyle) ? initialStyle : validValues[0];
}

// Helper to get the cost for an extra based on value and unit
function getExtraCost(extra, value, guestCount) {
  if (!extra || value === 0) return 0;
  const tier = value <= 0 ? 'zero' : value <= 25 ? 'low' : value <= 50 ? 'mid' : value <= 75 ? 'high' : 'luxury';
  let base = extra.us[tier] || 0;
  if (extra.unit === 'per guest') return base * guestCount;
  if (extra.unit === 'per invite') return base * Math.max(guestCount, 1); // fallback to guestCount for invites
  if (extra.unit === 'per night') return base * 1; // could be multiplied by nights if you add that
  if (extra.unit === 'per hour') return base * 1; // could be multiplied by hours if you add that
  return base;
}

// Helper to determine if any extras are nonzero
function getActiveExtras(extrasBudgets) {
  return Object.keys(extrasBudgets).filter(key => extrasBudgets[key] > 0);
}

// Helper to get available extras not yet added
function getAvailableExtras(extras, selectedKeys) {
  return extras.filter(e => !selectedKeys.includes(e.key));
}

// ErrorBoundary for debugging
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ color: 'red', padding: 16, background: '#fff0f0', borderRadius: 8 }}>
          <h2>Something went wrong in the Extras section.</h2>
          <pre style={{ fontSize: 12 }}>{this.state.error && this.state.error.toString()}</pre>
          <pre style={{ fontSize: 10 }}>{this.state.errorInfo && this.state.errorInfo.componentStack}</pre>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function BudgetBuilder() {
  const [guestCount, setGuestCount] = useState(80);
  const [style, setStyle] = useState(getValidInitialStyle());
  const [categoryBudgets, setCategoryBudgets] = useState(INITIAL_CATEGORY_BUDGETS);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [extrasBudgets, setExtrasBudgets] = useState(INITIAL_EXTRAS_BUDGETS);
  const [showExtras, setShowExtras] = useState(false);

  useEffect(() => {
    try {
      setError(null);
      const calculatedResults = calculateBudget({ 
        guestCount, 
        style, 
        categories: CATEGORIES,
        categoryBudgets 
      });

      // Add extras to the results
      let extrasBreakdown = [];
      let extrasTotal = 0;
      EXTRAS.forEach(extra => {
        const value = extrasBudgets[extra.key];
        if (value > 0) {
          const cost = getExtraCost(extra, value, guestCount);
          extrasBreakdown.push({
            category: extra.label + (extra.unit ? ` (${extra.unit})` : ''),
            us: cost,
            mexico: null // or 0, if you want to show Mexico for extras in the future
          });
          extrasTotal += cost;
        }
      });

      // Merge extras into breakdown and totals
      if (extrasBreakdown.length > 0) {
        calculatedResults.breakdown = [...calculatedResults.breakdown, ...extrasBreakdown];
        calculatedResults.usTotal += extrasTotal;
      }
      setResults(calculatedResults);
    } catch (err) {
      console.error('Error calculating budget:', err);
      setError(err.message);
    }
  }, [guestCount, style, categoryBudgets, extrasBudgets]);

  const handleReset = () => {
    setGuestCount(80);
    setStyle(getValidInitialStyle());
    setCategoryBudgets(INITIAL_CATEGORY_BUDGETS);
    setError(null);
  };

  const handleCategoryBudgetChange = (category, value) => {
    setCategoryBudgets(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleExtraBudgetChange = (key, value) => {
    setExtrasBudgets(prev => ({
      ...prev,
      [key]: value
    }));
  };

  if (error) {
    return (
      <div className="min-h-[80vh] bg-[#fafbfc] flex flex-col items-center justify-start py-12 px-4">
        <div className="bg-white rounded-3xl shadow-xl p-10 mb-10 border border-[#ececec] max-w-2xl w-full">
          <div className="text-center text-red-500">
            <h3 className="text-2xl font-bold mb-2">Error Calculating Budget</h3>
            <p className="mb-4">{error}</p>
            <button
              onClick={handleReset}
              className="bg-[#bfa76a] text-white font-bold py-4 px-8 rounded-full text-lg shadow hover:shadow-lg hover:bg-[#a68d5d] active:scale-95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#bfa76a]"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-[#fafbfc] flex flex-col items-center justify-start py-12 px-4">
      <div className="bg-white rounded-3xl shadow-xl p-10 mb-10 border border-[#ececec] max-w-2xl w-full">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-playfair text-[#181c24] font-bold mb-3 tracking-tight">Destination Wedding Budget Builder</h2>
          <p className="text-base text-[#888] font-medium">Compare average wedding costs in the U.S. vs. Mexico</p>
        </div>

        <div className="bg-[#fafbfc] rounded-2xl p-6 mb-8">
          <BudgetHeader
            guestCount={guestCount}
            style={style}
            onGuestCountChange={setGuestCount}
            onStyleChange={setStyle}
          />
        </div>

        <div className="space-y-6">
          <div className="bg-[#fcfcfd] rounded-xl p-4 md:p-6 shadow-sm">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[#181c24]">Adjust Your Budget Preferences</h3>
              <button
                onClick={handleReset}
                className="text-sm text-[#888] hover:text-[#181c24] transition-colors"
              >
                Reset All
              </button>
            </div>
            <div className="space-y-4">
              {CATEGORIES.map(category => (
                <CategoryBudgetControls
                  key={category}
                  category={category}
                  value={categoryBudgets[category]}
                  onChange={(value) => handleCategoryBudgetChange(category, value)}
                  costRanges={{
                    us: US_COST_RANGES[category],
                    mexico: MEXICO_COST_RANGES[category]
                  }}
                  guestCount={guestCount}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Wedding Extras Section (always visible) */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">💍</span>
            <h3 className="text-lg font-semibold text-[#bfa76a]">Wedding Extras</h3>
          </div>
          <ErrorBoundary>
            <ExtrasChipSelectorSection
              extras={EXTRAS}
              extrasBudgets={extrasBudgets}
              setExtrasBudgets={setExtrasBudgets}
              guestCount={guestCount}
            />
          </ErrorBoundary>
        </div>

        {results && <div className="mt-12"><BudgetResults results={results} onReset={handleReset} /></div>}
      </div>
    </div>
  );
}

// Chip/Tag Selector for Extras
function ExtrasChipSelectorSection({ extras, extrasBudgets, setExtrasBudgets, guestCount }) {
  const addedKeys = Object.keys(extrasBudgets).filter(key => extrasBudgets[key] > 0);
  const availableExtras = extras.filter(e => !addedKeys.includes(e.key));

  // Debug: log what is being rendered
  console.log('availableExtras', availableExtras);
  console.log('addedKeys', addedKeys);

  function handleAddExtra(key) {
    setExtrasBudgets(prev => ({ ...prev, [key]: 25 }));
  }

  function handleRemoveExtra(key) {
    setExtrasBudgets(prev => ({ ...prev, [key]: 0 }));
  }

  return (
    <div className="mt-4 bg-[#fcfcfd] rounded-xl p-4 md:p-6 shadow-sm">
      <div className="flex flex-wrap gap-2 mb-6">
        {availableExtras.length === 0 ? (
          <span className="text-xs text-[#bbb]">All extras added</span>
        ) : (
          <React.Fragment>
            {availableExtras.map(extra => {
              if (!extra || typeof extra !== 'object') {
                console.error('Invalid extra in availableExtras:', extra);
                return null;
              }
              if (typeof extra.label !== 'string') {
                console.error('Invalid extra.label:', extra.label, extra);
                return null;
              }
              return (
                <button
                  key={extra.key}
                  className="px-3 py-1 rounded-full bg-[#f7f6f3] text-xs font-medium text-[#bfa76a] border border-[#f3e6c6] hover:bg-[#f3e6c6] transition"
                  onClick={() => handleAddExtra(extra.key)}
                  type="button"
                >
                  {extra.label}{extra.unit && extra.unit !== 'per guest' ? ` (${extra.unit})` : ''}
                </button>
              );
            })}
          </React.Fragment>
        )}
      </div>
      {addedKeys.length === 0 && (
        <div className="text-center text-xs text-[#bbb] mb-4">No extras selected yet.</div>
      )}
      <div className="space-y-4">
        <React.Fragment>
          {addedKeys.map(key => {
            const extra = extras.find(e => e.key === key);
            if (!extra || typeof extra !== 'object') {
              console.error('Invalid extra in addedKeys:', extra, 'for key:', key);
              return null;
            }
            const value = extrasBudgets[extra.key];
            if (typeof value !== 'number') {
              console.error('Non-number value in extrasBudgets:', value, 'for key:', key);
              return null;
            }
            return (
              <div key={key} className="flex items-center gap-3 py-3 px-2 rounded-lg bg-[#f7f6f3] hover:bg-[#f3e6c6]/40 transition">
                <div className="flex-1">
                  <CategoryBudgetControls
                    category={extra.key}
                    value={value}
                    onChange={v => {
                      if (typeof v === 'number') {
                        setExtrasBudgets(prev => ({ ...prev, [extra.key]: v }));
                      } else {
                        console.error('Invalid value for extra:', v);
                      }
                    }}
                    costRanges={{ us: extra.us }}
                    guestCount={guestCount}
                    unit={extra.unit}
                  />
                </div>
                <button
                  className="ml-2 text-[#bfa76a] hover:text-[#e57373] p-2 rounded-full transition"
                  onClick={() => handleRemoveExtra(key)}
                  title="Remove extra"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            );
          })}
        </React.Fragment>
      </div>
    </div>
  );
} 